/**
 * Claim rules for the simplified teaching variant — pure functions over
 * concealed hands + open melds. No engine state, no canvas: every claim's
 * *availability* is decided here; the state machine (`GameState.ts`) owns
 * *applying* the chosen claim.
 *
 * Priority when several seats claim the same discard: Ron > Pung > Chi, and
 * among equal-rank claimers the seat closest after the discarder in turn
 * order wins. Chi is legal only for the discarder's right neighbour (the
 * next seat in turn order) — enforced in `availableClaims`, not `chiOptions`.
 */

import {
  COPIES_PER_KIND,
  RANKS,
  SEAT_COUNT,
  countByKey,
  isSameKind,
  tileKey,
  type Meld,
  type Seat,
  type Tile,
  type TileKind,
} from './types.js';
import type { Ruleset } from './ruleset/types.js';
import { isWinningHand } from './WinChecker.js';
export { resolveClaimPriority, type Claim } from './claimPriority.js';

/** Concealed tiles a Pung needs to match a discard (the discard is the third). */
const PUNG_MATCH_NEEDED = 2;
/** Concealed tiles an exposed Kong needs to match a discard (the discard is the fourth). */
export const KONG_MATCH_NEEDED = 3;

/** Offsets, relative to the discard's rank, of the two hand tiles a Chi uses. */
const CHI_WINDOWS: readonly (readonly [number, number])[] = [
  [-2, -1],
  [-1, 1],
  [1, 2],
];

/** A concrete Chi: the two concealed tiles that flank the discard in a run. */
export interface ChiOption {
  readonly tiles: readonly [Tile, Tile];
}

/** Per-seat claim availability against a single discard. */
export interface SeatClaims {
  readonly seat: Seat;
  readonly canRon: boolean;
  readonly canPung: boolean;
  readonly canKong: boolean;
  readonly chi: readonly ChiOption[];
}

/** Concealed tiles in `hand` matching `tile`'s kind. */
function matchCount(hand: readonly Tile[], tile: Tile): number {
  let matches = 0;
  for (const held of hand) {
    if (isSameKind(held, tile)) matches++;
  }
  return matches;
}

/** True iff `hand` holds ≥ 2 tiles matching `tile` (open triplet from a discard). */
export function canPung(hand: readonly Tile[], tile: Tile): boolean {
  return matchCount(hand, tile) >= PUNG_MATCH_NEEDED;
}

/** True iff `hand` holds ≥ 3 tiles matching `tile` (exposed Kong, ruleset-gated). */
export function canExposedKong(hand: readonly Tile[], tile: Tile, ruleset: Ruleset): boolean {
  return ruleset.kongEnabled && matchCount(hand, tile) >= KONG_MATCH_NEEDED;
}

/**
 * Every tile-kind `hand` holds all 4 concealed copies of — a self-declared
 * Kong on the holder's own turn. Ruleset-gated; can yield more than one option.
 */
export function concealedKongOptions(hand: readonly Tile[], ruleset: Ruleset): TileKind[] {
  if (!ruleset.kongEnabled) return [];
  const counts = countByKey(hand);
  const options: TileKind[] = [];
  for (const [key, count] of counts) {
    if (count < COPIES_PER_KIND) continue;
    const representative = hand.find((t) => tileKey(t) === key);
    if (representative !== undefined) options.push(representative);
  }
  return options;
}

/**
 * Every distinct run `hand` can form around suit `tile`. Honors never run, so
 * an honor discard yields none. Each option names the two concealed tiles used
 * (by identity, so the caller can remove those exact copies).
 */
export function chiOptions(hand: readonly Tile[], tile: Tile): ChiOption[] {
  if (tile.kind !== 'suit') return [];
  const options: ChiOption[] = [];
  for (const [lo, hi] of CHI_WINDOWS) {
    const r1 = tile.rank + lo;
    const r2 = tile.rank + hi;
    if (r1 < 1 || r1 > RANKS.length || r2 < 1 || r2 > RANKS.length) continue;
    const t1 = hand.find((t) => t.kind === 'suit' && t.suit === tile.suit && t.rank === r1);
    const t2 = hand.find((t) => t.kind === 'suit' && t.suit === tile.suit && t.rank === r2);
    if (t1 !== undefined && t2 !== undefined) options.push({ tiles: [t1, t2] });
  }
  return options;
}

/** True iff `tile` completes `hand` (+ `melds`) into a winning hand under `ruleset`. */
export function canRon(
  hand: readonly Tile[],
  melds: readonly Meld[],
  tile: Tile,
  ruleset: Ruleset,
): boolean {
  return isWinningHand([...hand, tile], melds.length, ruleset);
}

/** True iff `melds` has an exposed Pung matching `drawnTile` (self-drawn Added Kong, gated). */
export function addedKongAvailable(
  melds: readonly Meld[],
  drawnTile: Tile,
  ruleset: Ruleset,
): boolean {
  if (!ruleset.kongEnabled) return false;
  return melds.some(
    (meld) => meld.type === 'pung' && meld.tiles.some((t) => isSameKind(t, drawnTile)),
  );
}

/** Chi is legal only for the seat immediately after the discarder in turn order. */
function chiSeat(discarder: Seat): Seat {
  return ((discarder + 1) % SEAT_COUNT) as Seat;
}

/**
 * Claim availability for every seat other than `discarder`. Ron, Pung, and
 * Kong are open to any seat; Chi only to the discarder's right neighbour.
 * Seats with no available claim are omitted.
 */
export function availableClaims(
  hands: readonly (readonly Tile[])[],
  melds: readonly (readonly Meld[])[],
  discard: Tile,
  discarder: Seat,
  ruleset: Ruleset,
): SeatClaims[] {
  const result: SeatClaims[] = [];
  const runSeat = chiSeat(discarder);
  for (let s = 0; s < SEAT_COUNT; s++) {
    if (s === discarder) continue;
    const seat = s as Seat;
    const hand = hands[seat] ?? [];
    const seatMelds = melds[seat] ?? [];
    const claims: SeatClaims = {
      seat,
      canRon: canRon(hand, seatMelds, discard, ruleset),
      canPung: canPung(hand, discard),
      canKong: canExposedKong(hand, discard, ruleset),
      chi: seat === runSeat ? chiOptions(hand, discard) : [],
    };
    if (claims.canRon || claims.canPung || claims.canKong || claims.chi.length > 0) {
      result.push(claims);
    }
  }
  return result;
}
